import { FC } from 'react'
import { Box, Chip, Typography } from '@mui/material'
import { CheckCircleOutlined, HourglassEmptyOutlined } from '@mui/icons-material'
import { useIntl } from 'react-intl'
import dayjs from 'dayjs'
import ClipboardButton from 'components/ClipboardButton'
import {
  InsuranceConsentPropertyType,
  InsuranceConsentResponse
} from 'services/insurance/insurance.hooks'

interface PropertyDetailsProps {
  consent: InsuranceConsentResponse
}

const PropertyDetails: FC<PropertyDetailsProps> = ({ consent }) => {
  const { formatMessage } = useIntl()
  const { leaseContractNumber, propertyType, insuranceAmount, isConfirmed, confirmedAt } = consent

  const propertyTypeLabels: Record<InsuranceConsentPropertyType, string> = {
    residential: formatMessage({
      id: 'insuranceConsent.propertyTypeResidential',
      defaultMessage: 'Mieszkanie'
    }),
    hotel: formatMessage({
      id: 'insuranceConsent.propertyTypeHotel',
      defaultMessage: 'Hotel'
    }),
    hotel_wis: formatMessage({
      id: 'insuranceConsent.propertyTypeHotelWis',
      defaultMessage: 'Hotel WIS'
    })
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1,
          mb: 2
        }}>
        <Typography variant='h6' component='h2'>
          {formatMessage({
            id: 'insuranceConsent.propertyInfo',
            defaultMessage: 'Informacje o umowie'
          })}
        </Typography>
        {isConfirmed ? (
          <Chip
            icon={<CheckCircleOutlined />}
            color='success'
            label={formatMessage({
              id: 'insuranceConsent.statusConfirmed',
              defaultMessage: 'Potwierdzona'
            })}
          />
        ) : (
          <Chip
            icon={<HourglassEmptyOutlined />}
            color='warning'
            label={formatMessage({
              id: 'insuranceConsent.statusPending',
              defaultMessage: 'Oczekuje na potwierdzenie'
            })}
          />
        )}
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1,
          mb: 1
        }}>
        <Typography variant='body2' color='text.secondary'>
          {formatMessage({
            id: 'insuranceConsent.contractNumber',
            defaultMessage: 'Numer umowy'
          })}
          :
        </Typography>
        <Typography variant='body1' sx={{ fontWeight: 500 }}>
          {leaseContractNumber || '—'}
        </Typography>
        {!!leaseContractNumber && <ClipboardButton data={leaseContractNumber} />}
      </Box>
      {!!insuranceAmount && (
        <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: 1, mb: 1 }}>
          <Typography variant='body2' color='text.secondary'>
            {formatMessage({
              id: 'insuranceConsent.insuranceAmount',
              defaultMessage: 'Kwota ubezpieczenia'
            })}
            :
          </Typography>
          <Typography variant='h6' component='span' color='primary' sx={{ fontWeight: 700 }}>
            {insuranceAmount} PLN
          </Typography>
        </Box>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
        <Typography variant='body2' color='text.secondary'>
          {formatMessage({
            id: 'insuranceConsent.propertyType',
            defaultMessage: 'Typ nieruchomości'
          })}
          :
        </Typography>
        <Typography variant='body1'>{propertyTypeLabels[propertyType] || propertyType}</Typography>
      </Box>
      {isConfirmed && !!confirmedAt && (
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Typography variant='body2' color='text.secondary'>
            {formatMessage({
              id: 'insuranceConsent.confirmedAt',
              defaultMessage: 'Data potwierdzenia'
            })}
            :
          </Typography>
          <Typography variant='body1'>{dayjs(confirmedAt).format('DD.MM.YYYY HH:mm')}</Typography>
        </Box>
      )}
    </Box>
  )
}

export default PropertyDetails
